import type { AudienceData } from "@common/interfaces/KYDMarketing";
import type {
  GuidedCampaignScheduleWire,
  GuidedSubmitBuildInput,
} from "@common/interfaces/adsManagerGuidedEdit";
import type { GuidedFlowDraft } from "../components/CreateCampaignModal";
import type { BudgetData } from "../components/GuidedBudgetEditor";
import { mapCustomAudiencesToServerFormat } from "../transform/customAudienceMapper";
import type { CreateCampaignFlowValues } from "../forms/campaignFlowForm";
import { buildBudgetDataForGuidedCreatePayload } from "./createCampaignBudgetPayload";
import type {
  CreativeBlock,
  GuidedScheduleInput,
  GuidedSubmitParams,
  GuidedTargetingInput,
} from "./types";

function toKeyValueList(
  arr: { key?: string | null; value?: string | null }[] | undefined
): { key: string; value: string }[] {
  if (!arr) return [];
  return arr.filter(
    (item): item is { key: string; value: string } =>
      typeof item?.key === "string" && typeof item?.value === "string"
  ) as { key: string; value: string }[];
}

function toGeoMarketsList(
  arr:
    | {
        key?: string | null;
        value?: string | null;
        name?: string;
        country?: string;
      }[]
    | undefined
): { key: string; value: string; name?: string; country?: string }[] {
  if (!arr) return [];
  return arr.filter(
    (
      item
    ): item is {
      key: string;
      value: string;
      name?: string;
      country?: string;
    } => typeof item?.key === "string" && typeof item?.value === "string"
  );
}

export function buildCreateCampaignPayload(
  draft: GuidedFlowDraft,
  values: CreateCampaignFlowValues
) {
  if (!draft.adAccountId) {
    throw new Error("No Ad Account ID provided. Please contact support.");
  }

  const customAudiencesPayload =
    !values.smart_targeting &&
    mapCustomAudiencesToServerFormat(values.custom_audiences ?? []);
  const targetingStrategy: "custom" | "template" = values.smart_targeting
    ? "template"
    : "custom";
  const targeting: GuidedTargetingInput = {
    strategy: targetingStrategy,
  };

  if (customAudiencesPayload?.length) {
    targeting.custom_audiences = customAudiencesPayload;
  }

  if (targetingStrategy === "custom") {
    // Always send `locations` (including empty arrays) so PUT includes `targeting.locations`
    // and the server runs merge + transformer (omit skipped that path when geo was cleared).
    targeting.locations = {
      countries: toKeyValueList(values.countries),
      geo_markets: toGeoMarketsList(values.geo_markets),
      regions: toKeyValueList(values.regions),
      cities: toKeyValueList(values.cities),
    };
    if (
      values.age_min != null ||
      values.age_max != null ||
      (values.genders != null && values.genders !== "all")
    ) {
      targeting.demographics = {
        age_min: values.age_min ?? undefined,
        age_max: values.age_max ?? undefined,
        genders: values.genders ?? undefined,
      };
    }
    const strategyIds = values.enabled_strategy_ids ?? [];
    if (strategyIds.length > 0) {
      targeting.enabled_strategy_ids = strategyIds;
    }
  }

  return {
    ad_account_id: draft.adAccountId,
    event_id: draft.eventId,
    eventTimezone: draft.eventTimezone,
    creatives: (values.creatives ?? []) as CreativeBlock[],
    budget: buildBudgetDataForGuidedCreatePayload(values),
    schedule: {
      start_at: values.schedule?.start_at ?? "",
      end_at: values.schedule?.end_at ?? "",
    } satisfies GuidedCampaignScheduleWire,
    targeting,
  };
}

type BuildSaveAdsetParamsInput = {
  draft: Exclude<GuidedFlowDraft, { mode: "create" }>;
  values: CreateCampaignFlowValues;
  isNewCreativeMode: boolean;
  showBudgetSection: boolean;
  showAudienceSection: boolean;
  addToAllCreativeAdsets: boolean;
  selectableCreativeTargetAdsetIds: string[];
  selectedCreativeTargetAdsetIds: string[];
};

/** App-only extension of {@link GuidedSubmitBuildInput} with guided-flow draft (create mode excluded). */
export type BuildGuidedSubmitParamsInput = GuidedSubmitBuildInput<
  CreativeBlock,
  BudgetData
> & {
  draft: Exclude<GuidedFlowDraft, { mode: "create" }>;
};

function buildGuidedSubmitParams(
  params: BuildGuidedSubmitParamsInput
): GuidedSubmitParams | null {
  const { draft, saveParams, targetAdsetIds } = params;

  switch (draft.mode) {
    case "edit_new_creative":
      if (!targetAdsetIds?.[0]) return null;
      return {
        ...saveParams,
        mode: "edit_new_creative",
        adset_id: targetAdsetIds[0],
      };
    case "edit":
      if (!draft.adsetId) return null;
      return {
        ...saveParams,
        mode: "edit",
        adset_id: draft.adsetId,
      };
    case "edit_new_adset":
      return {
        ...saveParams,
        mode: "edit_new_adset",
      };
    default:
      return null;
  }
}

export function buildSaveAdsetParams({
  draft,
  values,
  isNewCreativeMode,
  showBudgetSection,
  showAudienceSection,
  addToAllCreativeAdsets,
  selectableCreativeTargetAdsetIds,
  selectedCreativeTargetAdsetIds,
}: BuildSaveAdsetParamsInput) {
  const strategy: "custom" | "template" = values.smart_targeting
    ? "template"
    : "custom";
  const guidedEditPayload = draft.guidedEditPayload;
  const targetAdsetIds = isNewCreativeMode
    ? addToAllCreativeAdsets
      ? selectableCreativeTargetAdsetIds
      : selectedCreativeTargetAdsetIds
    : undefined;

  const saveParams: GuidedSubmitBuildInput<
    CreativeBlock,
    BudgetData
  >["saveParams"] = {
    campaign_id: draft.campaignId,
    creatives: (values.creatives ?? []) as CreativeBlock[],
  };

  const adsetName = guidedEditPayload?.adset_name ?? "";
  if (!isNewCreativeMode && adsetName.trim().length > 0) {
    saveParams.adset_name = adsetName;
  }

  if (isNewCreativeMode) {
    saveParams.creative_propagation_mode = "append_last_creative";
    saveParams.target_adset_ids = targetAdsetIds;
  }

  if (showBudgetSection) {
    saveParams.budget = values.budget as BudgetData;
    saveParams.schedule = {
      start_at: values.schedule?.start_at ?? "",
      end_at: values.schedule?.end_at ?? "",
    };
  } else if (
    (draft.mode === "edit" || draft.mode === "edit_new_adset") &&
    ((values.budget?.type === "scheduled"
      ? (values.budget?.segments?.length ?? 0) > 0 &&
        (values.budget?.segments ?? []).some((s) => {
          const n = Number(s.amount?.amount);
          return Number.isFinite(n) && n > 0;
        })
      : values.budget?.amount?.amount != null &&
        values.budget?.amount?.amount !== "") ||
      Boolean(values.schedule?.start_at))
  ) {
    // Edit mode with hidden budget section: still send budget/schedule when user edited via popup or form
    const hasBudgetForHiddenSection =
      values.budget?.type === "scheduled"
        ? (values.budget?.segments?.length ?? 0) > 0 &&
          (values.budget?.segments ?? []).some((s) => {
            const n = Number(s.amount?.amount);
            return Number.isFinite(n) && n > 0;
          })
        : values.budget?.amount?.amount != null &&
          values.budget?.amount?.amount !== "";
    if (hasBudgetForHiddenSection) {
      saveParams.budget = values.budget as BudgetData;
    }
    if (values.schedule?.start_at && values.schedule?.end_at) {
      saveParams.schedule = {
        start_at: values.schedule.start_at,
        end_at: values.schedule.end_at,
      };
    }
  }

  if (showAudienceSection) {
    saveParams.targeting = {
      strategy,
      custom_audiences: values.custom_audiences as AudienceData[] | undefined,
    };
    // Guided edit: manual geo/demographics/interests when Advantage+ is off. `enabled_strategy_ids`
    // (manual org strategy toggles) is create-only (see `buildCreateCampaignPayload`); edit flow does not surface toggles today.
    if (strategy === "custom") {
      saveParams.targeting.locations = {
        countries: toKeyValueList(values.countries),
        geo_markets: toGeoMarketsList(values.geo_markets),
        regions: toKeyValueList(values.regions),
        cities: toKeyValueList(values.cities),
      };
      if (
        values.age_min != null ||
        values.age_max != null ||
        (values.genders != null && values.genders !== "all")
      ) {
        saveParams.targeting.demographics = {
          age_min: values.age_min ?? undefined,
          age_max: values.age_max ?? undefined,
          genders: values.genders ?? undefined,
        };
      }
    }
  }

  const guidedSubmitParams = buildGuidedSubmitParams({
    draft,
    saveParams,
    targetAdsetIds,
  });

  return { guidedSubmitParams };
}
